import clsx from "clsx";
import Button from "components/common_components/Button";
import Card from "components/common_components/Card";
import Icon from "components/common_components/Icon";
import React, { Fragment, useEffect, useState } from "react";
import { hasElements } from "utils/arrays";
import PropTypes from "prop-types";
import { TextField } from "@material-ui/core";

const Categories = ({ categories, visible, onChange, onBackClick, selected }) => {
    const [filter, setFilter] = useState("");
    const [filteredCategories, setFilteredCategories] = useState(categories);
    const [visibleIds, setVisibleIds] = useState(undefined);

    const onFilterChange = (e) => {
        setFilter(e.target.value);
    };

    useEffect(() => {
        const filterByName = (category) => category.category.toLowerCase().includes(filter.toLowerCase());
        const isParent = (category) => category.parentCategory === null;
        const parents = hasElements(categories) ? categories.filter(isParent) : [];

        if (!filter) {
            setFilteredCategories(parents);
            setVisibleIds(undefined);
        } else if (hasElements(categories)) {
            const matches = categories.filter(filterByName);
            const visible = new Set();
            const rootIds = new Set();

            const byId = new Map(categories.map((c) => [c.id, c]));

            matches.forEach((match) => {
                let current = match;
                while (current) {
                    visible.add(current.id);
                    if (!current.parentCategory) {
                        rootIds.add(current.id);
                        break;
                    }
                    current = byId.get(current.parentCategory.id);
                }
            });

            const roots = hasElements(categories)
                ? categories.filter((c) => rootIds.has(c.id))
                : [];

            setFilteredCategories(roots);
            setVisibleIds(visible);
        } else {
            setFilteredCategories([]);
            setVisibleIds(undefined);
        }

        return () => {
            setFilteredCategories([]);
            setVisibleIds(undefined);
        };
    }, [filter, categories]);

    const renderCategory = (category) => {
        return (
            <>
                <Card onClick={() => onChange(category)}>
                    <span>{category.category}</span>
                    <Icon name="arrow" color="secondary" size="xs" style={{ transform: "rotate(180deg)" }} />
                </Card>
                <div className="sub-categories">
                    {(
                        categories
                            .filter((x) => x.parentCategory?.id === category.id)
                            .filter((x) => (visibleIds ? visibleIds.has(x.id) : true)) || []
                    ).map((subCategory) => (
                        <Fragment key={subCategory.id}>{renderCategory(subCategory)}</Fragment>
                    ))}
                </div>
            </>
        );
    };

    return (
        <div
            className={clsx("layout categories-container", {
                visible: visible,
                invisible: !visible,
            })}
        >
            {visible && (
                <>
                    <Button secondary link onClick={selected ? onBackClick : undefined}>
                        <span>
                            <h3>Category</h3>
                        </span>
                        {selected && <Icon name="cancel" color="secondary" size="xs" />}
                    </Button>
                    <hr />
                    {!selected && (
                        <>
                            <small>
                                <i>Select a category under which the form will be created</i>
                            </small>
                            <TextField
                                name="categories-filter-input"
                                className="categories-filter-input"
                                type="text"
                                placeholder="Search categories..."
                                value={filter}
                                onChange={onFilterChange}
                            />
                            <div className="categories">
                                {filteredCategories.map((category) => (
                                    <Fragment key={category.id}>{renderCategory(category)}</Fragment>
                                ))}
                            </div>
                        </>
                    )}
                    {selected && (
                        <div className="categories">
                            <Card>
                                <span>{selected.category}</span>
                            </Card>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

Categories.propTypes = {
    categories: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            category: PropTypes.string,
            description: PropTypes.string,
            parentCategory: PropTypes.shape({
                id: PropTypes.string,
                category: PropTypes.string,
            }),
        })
    ),
    visible: PropTypes.bool,
    onChange: PropTypes.func,
    selected: PropTypes.shape({
        id: PropTypes.string,
        category: PropTypes.string,
        description: PropTypes.string,
        parentCategory: PropTypes.shape({
            id: PropTypes.string,
            category: PropTypes.string,
        }),
    }),
    onBackClick: PropTypes.func,
};

Categories.defaultProps = {
    categories: [],
    visible: true,
    onChange: () => {},
    selected: null,
    onBackClick: () => {},
};

export default Categories;
